import React, { FC } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import {
    addComposerSharedPrice,
    applyComposerSharedPriceEdit,
    getComposerPriceConditionKey,
    getComposerSharedOptionPrices,
    removeComposerSharedPrice
} from '../../../configurator-composer/composer/helpers/composerBulkOptionPrices';
import { ComposerBlockList, ComposerNewButton } from '../composerBlock';
import { ComposerUnsupportedNote } from '../composerWorkspace';
import { ComposerAttributeOptionNode, ComposerEcommercePlatform, ComposerPricingNode } from './composerAttributesModels';
import { ComposerPriceCard } from './composerPriceCard';

const NewPriceButton = styled(ComposerNewButton)`
    margin-top: 10px;
`;

/**
 * I PREZZI CONDIVISI DELLE OPZIONI SCELTE (utente, 2026-10-21), accanto alle azioni condivise. Una card per ogni prezzo
 * che tutte le opzioni hanno con le stesse condizioni: l'importo e le condizioni scritti qui valgono per tutte, e con
 * importi diversi la card dice «Mixed values». «New price» mette un prezzo «Always» dove manca. Per Shopify e BigCommerce
 * il prezzo di un'opzione è un addebito o una variante dello store, e farli in blocco non è verificato: lì si resta a uno
 * alla volta.
 */
export const ComposerSharedPrices: FC<{ options: ComposerAttributeOptionNode[] }> = ({ options }) => {
    const [attributes, updateAttribute, ecommerceSettings] = useComposerStore((store) => [store.attributes, store.updateAttribute, store.ecommerceSettings]);
    const platform = ecommerceSettings?.ecommercePlatform ?? ComposerEcommercePlatform.None;
    const sharedPrices = React.useMemo(() => getComposerSharedOptionPrices(options, attributes), [options, attributes]);
    const [openIds, setOpenIds] = React.useState<string[]>([]);
    const excludeAttributeIDs = Array.from(new Set(options.map((option) => option.attributeID)));

    if (![ComposerEcommercePlatform.WooCommerce, ComposerEcommercePlatform.Shopify, ComposerEcommercePlatform.BigCommerce].includes(platform))
        return <ComposerUnsupportedNote>{T._('Prices are not available for this store.', 'Admin')}</ComposerUnsupportedNote>;
    if (platform !== ComposerEcommercePlatform.WooCommerce)
        return (
            <ComposerUnsupportedNote>
                {T._('In this store the price of an option is a charge or a store variant: set it one option at a time.', 'Admin')}
            </ComposerUnsupportedNote>
        );

    // La card si riconosce dalla riga della prima opzione, non dalle condizioni: cambiandole resta aperta.
    const idOf = (entry: { attributeID: string; index: number }) => `${entry.attributeID}:${entry.index}`;
    const add = () => {
        addComposerSharedPrice(options, attributes).forEach(updateAttribute);
        const always = getComposerPriceConditionKey({ links: [] } as unknown as ComposerPricingNode);
        const created = getComposerSharedOptionPrices(options, useComposerStore.getState().attributes).find((shared) => shared.key === always);
        if (created) setOpenIds((current) => [...current, idOf(created.entries[0])]);
    };

    return (
        <>
            {sharedPrices.length === 0 ? (
                <ComposerUnsupportedNote>
                    {T._('The selected options have no price in common. A price is shared when every option has it with the same conditions.', 'Admin')}
                </ComposerUnsupportedNote>
            ) : (
                <ComposerBlockList>
                    {sharedPrices.map((shared) => {
                        const id = idOf(shared.entries[0]);
                        return (
                            <ComposerPriceCard
                                key={id}
                                id={`shared-price-${id}`}
                                pricing={shared.representative}
                                attributes={attributes}
                                excludeAttributeIDs={excludeAttributeIDs}
                                open={openIds.includes(id)}
                                onToggleOpen={() =>
                                    setOpenIds((current) => (current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id]))
                                }
                                caption={shared.hasMixedValues ? T._('Mixed values', 'Admin') : undefined}
                                onChange={(patch) =>
                                    applyComposerSharedPriceEdit(useComposerStore.getState().attributes, shared.entries, patch).forEach(updateAttribute)
                                }
                                onRemove={() => {
                                    removeComposerSharedPrice(useComposerStore.getState().attributes, shared.entries).forEach(updateAttribute);
                                    setOpenIds((current) => current.filter((candidate) => candidate !== id));
                                }}
                            />
                        );
                    })}
                </ComposerBlockList>
            )}
            <NewPriceButton type='button' onClick={add}>
                <PlusIcon />
                {T._('New price', 'Admin')}
            </NewPriceButton>
        </>
    );
};
